import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, Eye, CheckCircle2, ArrowLeftRight,
  TrendingUp, Clock, XCircle, ChevronLeft, ChevronRight, Loader2,
  SlidersHorizontal, RotateCcw, Filter,
} from 'lucide-react';
import { adminAPI } from '../../../services/api';
import { toast } from 'react-toastify';
import ActionMenu from '../../shared/ActionMenu';

const C = {
  bg: '#f4f4f9', surface: '#FFFFFF', border: '#e8e8f1', borderSoft: '#f0f0f6',
  ink: '#14142b', body: '#4a4a6a', muted: '#6e6e86',
  primary: '#7c3aed', primarySoft: '#f1ebff', primaryTint: '#ddd6fe',
  success: '#10B981', successSoft: '#D1FAE5',
  warning: '#F59E0B', warnSoft: '#FEF3C7',
  danger: '#EF4444', dangerSoft: '#FEE2E2',
  radius: 14, radiusSm: 8,
};

const fmtINR  = (n) => '₹' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const fmtTime = (iso) => iso ? new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '';

/* ── Status Badge ── */
const StatusBadge = ({ status }) => {
  const map = {
    success:   { bg: C.successSoft, color: C.success,  label: 'Success'  },
    pending:   { bg: C.warnSoft,    color: C.warning,  label: 'Pending'  },
    failed:    { bg: C.dangerSoft,  color: C.danger,   label: 'Failed'   },
    initiated: { bg: '#F1F5F9',     color: C.muted,    label: 'Initiated'},
    refunded:  { bg: C.primarySoft, color: C.primary,  label: 'Refunded' },
  };
  const s = map[(status || '').toLowerCase()] || { bg: '#F1F5F9', color: C.muted, label: status };
  return <span style={{ padding: '3px 10px', borderRadius: 999, background: s.bg, color: s.color, fontSize: 11, fontWeight: 700, textTransform: 'capitalize', whiteSpace: 'nowrap' }}>{s.label}</span>;
};

/* ── Type Badge ── */
const TypeBadge = ({ type }) => {
  const map = {
    deposit:    { bg: C.successSoft, color: C.success },
    withdrawal: { bg: C.dangerSoft,  color: C.danger  },
    commission: { bg: C.primarySoft, color: C.primary  },
    refund:     { bg: C.warnSoft,    color: C.warning  },
  };
  const s = map[(type || '').toLowerCase()] || { bg: '#F1F5F9', color: C.muted };
  return <span style={{ padding: '3px 10px', borderRadius: 999, background: s.bg, color: s.color, fontSize: 11, fontWeight: 700, textTransform: 'capitalize', whiteSpace: 'nowrap' }}>{type || '—'}</span>;
};

/* ── KPI Card ── */
const KpiCard = ({ label, value, sub, color, bg, Icon, active, onClick }) => {
  const [hov, setHov] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: C.surface, borderRadius: C.radius,
        border: `1.5px solid ${active ? color : (hov ? C.primaryTint : C.border)}`,
        padding: '18px 20px', cursor: onClick ? 'pointer' : 'default',
        boxShadow: active ? `0 4px 20px ${color}20` : hov ? '0 4px 16px rgba(20,20,43,0.06)' : '0 1px 4px rgba(20,20,43,0.04)',
        transition: 'all .18s', display: 'flex', flexDirection: 'column', gap: 12,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 6 }}>{label}</div>
          <div style={{ fontSize: 26, fontWeight: 800, color: active ? color : C.ink, lineHeight: 1, letterSpacing: '-.02em', minHeight: 30, display: 'flex', alignItems: 'center' }}>{value}</div>
        </div>
        <div style={{ width: 40, height: 40, borderRadius: 10, background: active ? color : bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'background .18s' }}>
          <Icon size={18} color={active ? '#fff' : color} strokeWidth={2} />
        </div>
      </div>
      {sub && <div style={{ fontSize: 12, color: active ? color : C.muted, fontWeight: 500 }}>{sub}</div>}
    </div>
  );
};

/* ══════════════════════════════════════════════════ */
const AdminTransactions = () => {
  const navigate = useNavigate();
  const [loading,      setLoading]      = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [pagination,   setPagination]   = useState(null);
  const [verifying,    setVerifying]    = useState(null);
  const [showFilters,  setShowFilters]  = useState(false);
  const [filters, setFilters] = useState({
    search: '', type: 'all', status: 'all',
    date_from: '', date_to: '',
    page: 1, per_page: 15, sort_by: 'created_at', sort_order: 'desc',
  });
  const [stats, setStats] = useState({ total: 0, success: 0, pending: 0, failed: 0, initiated: 0, totalAmount: 0 });
  const [fetchKey, setFetchKey] = useState(0);

  useEffect(() => { fetchTransactions(); /* eslint-disable-next-line */ }, [fetchKey, filters.page, filters.status]);

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const params = { ...filters };
      if (params.type   === 'all') delete params.type;
      if (params.status === 'all') delete params.status;
      const r = await adminAPI.transactions.getAll(params);
      const txns = r.data.data.transactions;
      setTransactions(txns);
      setPagination(r.data.data.pagination);
      const succ = txns.filter(t => t.status === 'success');
      setStats({
        total:       txns.length,
        success:     succ.length,
        pending:     txns.filter(t => t.status === 'pending').length,
        failed:      txns.filter(t => t.status === 'failed').length,
        initiated:   txns.filter(t => t.status === 'initiated').length,
        totalAmount: succ.reduce((s, t) => s + parseFloat(t.amount || 0), 0),
      });
    } catch { toast.error('Failed to fetch transactions'); }
    finally { setLoading(false); }
  };

  const setFilter   = (k, v) => setFilters(p => ({ ...p, [k]: v }));
  const setPage     = (p)    => setFilters(f => ({ ...f, page: p }));
  const apply       = ()     => { setFilters(f => ({ ...f, page: 1 })); setFetchKey(k => k + 1); };
  const reset       = ()     => { setFilters({ search: '', type: 'all', status: 'all', date_from: '', date_to: '', page: 1, per_page: 15, sort_by: 'created_at', sort_order: 'desc' }); setFetchKey(k => k + 1); };
  const quickStatus = (s)    => { setFilters(f => ({ ...f, status: s, page: 1 })); };

  const verify = async (txnId) => {
    setVerifying(txnId);
    try { await adminAPI.transactions.verifyPayment(txnId); toast.success('Payment verified'); fetchTransactions(); }
    catch (e) { toast.error(e.response?.data?.message || 'Failed to verify'); }
    finally { setVerifying(null); }
  };

  const pages = (() => {
    if (!pagination) return [];
    const { last_page: t, current_page: c } = pagination;
    let s = Math.max(1, c - 2), e = Math.min(t, s + 4);
    if (e - s < 4) s = Math.max(1, e - 4);
    return Array.from({ length: e - s + 1 }, (_, i) => s + i);
  })();

  const inp = { height: 38, borderRadius: C.radiusSm, border: `1.5px solid ${C.border}`, padding: '0 12px', fontSize: 13, color: C.ink, background: C.surface, outline: 'none', fontFamily: 'inherit', transition: 'border-color .15s' };

  /* quick-filter tabs */
  const qTabs = [
    { label: 'All',       val: 'all',       count: stats.total,     color: C.primary },
    { label: 'Success',   val: 'success',   count: stats.success,   color: C.success },
    { label: 'Pending',   val: 'pending',   count: stats.pending,   color: C.warning },
    { label: 'Initiated', val: 'initiated', count: stats.initiated, color: C.muted   },
    { label: 'Failed',    val: 'failed',    count: stats.failed,    color: C.danger  },
  ];

  return (
    <div style={{ maxWidth: 1280, margin: '0 auto' }}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg) } }
        .txn-kpi-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; margin-bottom: 20px; }
        .txn-adv-grid { display: grid; grid-template-columns: 2fr 1fr 1fr 1fr 1fr 1fr; gap: 10px; margin-bottom: 12px; }
        @media (max-width: 1100px) { .txn-kpi-grid { grid-template-columns: repeat(2, 1fr); } .txn-adv-grid { grid-template-columns: 1fr 1fr 1fr; } }
        @media (max-width: 640px)  { .txn-kpi-grid { grid-template-columns: 1fr 1fr; } .txn-adv-grid { grid-template-columns: 1fr; } }
        .adm-inp:focus { border-color: #7c3aed !important; box-shadow: 0 0 0 3px rgba(124,58,237,0.10); }
      `}</style>

      {/* ── Page Header ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 22 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: C.ink, margin: 0, letterSpacing: '-.02em' }}>Transaction Management</h1>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: C.muted }}>Monitor and manage all payment transactions.</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => setShowFilters(v => !v)}
            style={{ height: 38, padding: '0 14px', borderRadius: C.radiusSm, background: showFilters ? C.primarySoft : C.surface, color: showFilters ? C.primary : C.body, border: `1.5px solid ${showFilters ? C.primaryTint : C.border}`, fontWeight: 600, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 7, fontFamily: 'inherit' }}>
            <SlidersHorizontal size={14} /> Filters {showFilters ? '▴' : '▾'}
          </button>
          <button onClick={reset}
            style={{ height: 38, padding: '0 14px', borderRadius: C.radiusSm, background: C.surface, color: C.body, border: `1.5px solid ${C.border}`, fontWeight: 600, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 7, fontFamily: 'inherit' }}>
            <RotateCcw size={13} /> Reset
          </button>
        </div>
      </div>

      {/* ── KPI Cards ── */}
      <div className="txn-kpi-grid">
        <KpiCard label="Total Transactions" value={pagination?.total ?? stats.total} sub="All time" color={C.primary} bg={C.primarySoft} Icon={ArrowLeftRight} />
        <KpiCard label="Successful"  value={stats.success}  sub={fmtINR(stats.totalAmount)} color={C.success} bg={C.successSoft} Icon={CheckCircle2}
          active={filters.status === 'success'} onClick={() => quickStatus(filters.status === 'success' ? 'all' : 'success')} />
        <KpiCard label="Pending"     value={stats.pending}  sub="Awaiting processing"  color={C.warning} bg={C.warnSoft}    Icon={Clock}
          active={filters.status === 'pending'} onClick={() => quickStatus(filters.status === 'pending' ? 'all' : 'pending')} />
        <KpiCard label="Failed"      value={stats.failed}   sub="Need attention"      color={C.danger}  bg={C.dangerSoft}  Icon={XCircle}
          active={filters.status === 'failed'} onClick={() => quickStatus(filters.status === 'failed' ? 'all' : 'failed')} />
      </div>

      {/* ── Quick Filter + Search Bar ── */}
      <div style={{ background: C.surface, borderRadius: C.radius, border: `1.5px solid ${C.border}`, marginBottom: 16, overflow: 'hidden' }}>

        {/* Top row: search + quick tabs */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 0, borderBottom: `1px solid ${C.borderSoft}` }}>
          <div style={{ position: 'relative', flex: 1, borderRight: `1px solid ${C.borderSoft}` }}>
            <Search size={14} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: C.muted, pointerEvents: 'none' }} />
            <input
              className="adm-inp"
              style={{ ...inp, width: '100%', border: 'none', height: 46, paddingLeft: 38, borderRadius: 0, background: 'transparent' }}
              placeholder="Search by transaction ID, user…"
              value={filters.search}
              onChange={e => setFilter('search', e.target.value)}
              onKeyDown={e => e.key === 'Enter' && apply()}
            />
          </div>
          {/* Quick Status Tabs */}
          <div style={{ display: 'flex', padding: '0 12px', gap: 4, flexWrap: 'nowrap', overflowX: 'auto' }}>
            {qTabs.map(({ label, val, count, color }) => (
              <button key={val} onClick={() => quickStatus(val)}
                style={{ height: 30, padding: '0 12px', borderRadius: 999, border: `1.5px solid ${filters.status === val ? color : 'transparent'}`, background: filters.status === val ? color : 'transparent', color: filters.status === val ? '#fff' : C.muted, fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 5, transition: 'all .15s' }}>
                {label}
                <span style={{ background: filters.status === val ? 'rgba(255,255,255,0.25)' : C.borderSoft, color: filters.status === val ? '#fff' : C.muted, fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 999, minWidth: 18, textAlign: 'center' }}>{count}</span>
              </button>
            ))}
          </div>
          <div style={{ padding: '0 12px', borderLeft: `1px solid ${C.borderSoft}` }}>
            <button onClick={apply} style={{ height: 36, padding: '0 18px', borderRadius: C.radiusSm, background: C.primary, color: '#fff', border: 'none', fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 7, whiteSpace: 'nowrap' }}>
              <Filter size={13} /> Apply
            </button>
          </div>
        </div>

        {/* Advanced Filters (collapsible) */}
        {showFilters && (
          <div style={{ padding: '14px 16px', background: C.bg }}>
            <div className="txn-adv-grid">
              <select className="adm-inp" style={{ ...inp, cursor: 'pointer' }} value={filters.type} onChange={e => setFilter('type', e.target.value)}>
                <option value="all">All Types</option>
                <option value="deposit">Deposit</option>
                <option value="withdrawal">Withdrawal</option>
                <option value="commission">Commission</option>
                <option value="refund">Refund</option>
              </select>
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 10, color: C.muted, pointerEvents: 'none', fontWeight: 600 }}>FROM</span>
                <input type="date" className="adm-inp" style={{ ...inp, paddingLeft: 52 }} value={filters.date_from} onChange={e => setFilter('date_from', e.target.value)} />
              </div>
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 10, color: C.muted, pointerEvents: 'none', fontWeight: 600 }}>TO</span>
                <input type="date" className="adm-inp" style={{ ...inp, paddingLeft: 30 }} value={filters.date_to} onChange={e => setFilter('date_to', e.target.value)} />
              </div>
              <select className="adm-inp" style={{ ...inp, cursor: 'pointer' }} value={filters.sort_order} onChange={e => setFilter('sort_order', e.target.value)}>
                <option value="desc">↓ Newest First</option>
                <option value="asc">↑ Oldest First</option>
              </select>
              <select className="adm-inp" style={{ ...inp, cursor: 'pointer' }} value={filters.per_page} onChange={e => setFilter('per_page', parseInt(e.target.value))}>
                <option value={15}>15 per page</option>
                <option value={25}>25 per page</option>
                <option value={50}>50 per page</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* ── Table ── */}
      <div style={{ background: C.surface, borderRadius: C.radius, border: `1.5px solid ${C.border}`, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 60, textAlign: 'center', color: C.muted }}>
            <Loader2 size={28} style={{ animation: 'spin .8s linear infinite', color: C.primary }} />
            <p style={{ marginTop: 12, fontSize: 13 }}>Loading transactions…</p>
          </div>
        ) : transactions.length === 0 ? (
          <div style={{ padding: 60, textAlign: 'center', color: C.muted }}>
            <ArrowLeftRight size={36} style={{ opacity: .3, marginBottom: 12 }} />
            <p style={{ fontSize: 13 }}>No transactions found.</p>
          </div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: C.bg, borderBottom: `1.5px solid ${C.border}` }}>
                    {['#', 'Transaction ID', 'User', 'Type', 'Amount', 'Gateway', 'Status', 'Date', 'Actions'].map(h => (
                      <th key={h} style={{ padding: '11px 16px', textAlign: 'left', fontWeight: 700, fontSize: 10.5, color: C.muted, textTransform: 'uppercase', letterSpacing: '.07em', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((t, i) => (
                    <tr key={t.id} style={{ borderBottom: `1px solid ${C.borderSoft}`, transition: 'background .12s' }}
                      onMouseEnter={e => e.currentTarget.style.background = C.primarySoft}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <td style={{ padding: '12px 16px', color: C.muted, fontSize: 12 }}>#{t.id}</td>
                      <td style={{ padding: '12px 16px' }}>
                        <code style={{ fontSize: 11, color: C.primary, fontFamily: 'Menlo,Consolas,monospace', fontWeight: 700, background: C.primarySoft, padding: '2px 6px', borderRadius: 4 }}>{t.transaction_id}</code>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ fontWeight: 600, color: C.ink, fontSize: 13 }}>{t.user?.username}</div>
                        <div style={{ fontSize: 11, color: C.muted, fontFamily: 'Menlo,monospace' }}>{t.user?.email}</div>
                      </td>
                      <td style={{ padding: '12px 16px' }}><TypeBadge type={t.transaction_type} /></td>
                      <td style={{ padding: '12px 16px', fontWeight: 800, color: C.ink, fontSize: 14, whiteSpace: 'nowrap' }}>{fmtINR(t.amount)}</td>
                      <td style={{ padding: '12px 16px', fontSize: 12, color: C.muted, fontWeight: 600 }}>{t.payment_gateway || 'N/A'}</td>
                      <td style={{ padding: '12px 16px' }}><StatusBadge status={t.status} /></td>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ fontSize: 12, color: C.ink, fontWeight: 600 }}>{fmtDate(t.created_at)}</div>
                        <div style={{ fontSize: 11, color: C.muted }}>{fmtTime(t.created_at)}</div>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <ActionMenu items={[
                          { label: 'View Details', Icon: Eye, color: C.primary, bg: C.primarySoft, onClick: () => navigate(`/admin/transactions/${t.transaction_id}`) },
                          { label: 'Verify Payment', Icon: CheckCircle2, color: C.success, bg: C.successSoft, loading: verifying === t.transaction_id, onClick: () => verify(t.transaction_id), hidden: !(t.status === 'pending' && t.transaction_type === 'deposit') },
                        ]} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination && pagination.last_page > 1 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 20px', borderTop: `1px solid ${C.borderSoft}`, flexWrap: 'wrap', gap: 10 }}>
                <span style={{ fontSize: 12, color: C.muted }}>Showing <b style={{ color: C.ink }}>{pagination.from}–{pagination.to}</b> of <b style={{ color: C.ink }}>{pagination.total}</b> transactions</span>
                <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                  <button onClick={() => setPage(pagination.current_page - 1)} disabled={pagination.current_page === 1}
                    style={{ width: 32, height: 32, borderRadius: 7, border: `1.5px solid ${C.border}`, background: C.surface, cursor: pagination.current_page === 1 ? 'not-allowed' : 'pointer', color: C.body, display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: pagination.current_page === 1 ? .4 : 1 }}>
                    <ChevronLeft size={14} />
                  </button>
                  {pages.map(p => (
                    <button key={p} onClick={() => setPage(p)} style={{
                      width: 32, height: 32, borderRadius: 7, border: `1.5px solid ${p === pagination.current_page ? C.primary : C.border}`,
                      background: p === pagination.current_page ? C.primary : C.surface,
                      color: p === pagination.current_page ? '#fff' : C.body,
                      fontWeight: p === pagination.current_page ? 700 : 500, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit',
                    }}>{p}</button>
                  ))}
                  <button onClick={() => setPage(pagination.current_page + 1)} disabled={pagination.current_page === pagination.last_page}
                    style={{ width: 32, height: 32, borderRadius: 7, border: `1.5px solid ${C.border}`, background: C.surface, cursor: pagination.current_page === pagination.last_page ? 'not-allowed' : 'pointer', color: C.body, display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: pagination.current_page === pagination.last_page ? .4 : 1 }}>
                    <ChevronRight size={14} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminTransactions;
